import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { Table2, Plus, Upload, Trash2, FileSpreadsheet, FileText, Save, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

// ---------- helpers ----------
const uid = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : "id-" + Math.random().toString(36).slice(2) + Date.now());
const colLetter = (i) => { let s = ""; i++; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); } return s; };
const letterToIndex = (L) => { let n = 0; for (const ch of String(L).toUpperCase()) { n = n * 26 + (ch.charCodeAt(0) - 64); } return n - 1; };
const toNumber = (raw) => {
  if (raw == null) return NaN;
  let s = String(raw).trim().replace(/€/g, "").replace(/\s/g, "");
  if (s === "") return NaN;
  const hasC = s.includes(","), hasD = s.includes(".");
  if (hasC && hasD) s = s.replace(/\./g, "").replace(",", ".");
  else if (hasC) s = s.replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n;
};
const fmtEuro = (n) => (isNaN(n) ? "" : n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }) + " €");
const isFormula = (raw) => typeof raw === "string" && raw.trim().startsWith("=");
const colIdByLetter = (table, L) => { const i = letterToIndex(L); return table.columns[i] ? table.columns[i].id : null; };

function cellNumber(table, row, colId, tables, seen) {
  const raw = ((row.cells && row.cells[colId]) || "").trim();
  if (!isFormula(raw)) return toNumber(raw);
  const key = table.id + ":" + row.id + ":" + colId;
  if (seen.has(key)) return NaN;
  seen.add(key);
  try { return evalFormula(raw.slice(1), table, row, tables, seen); }
  catch { return NaN; }
  finally { seen.delete(key); }
}
function columnAgg(table, fn, colId, tables) {
  const vals = [];
  for (const r of table.rows) { const v = cellNumber(table, r, colId, tables, new Set()); if (!isNaN(v)) vals.push(v); }
  if (fn === "COUNT" || fn === "CONTA") return vals.length;
  if (vals.length === 0) return (fn === "SUM" || fn === "SOMMA") ? 0 : NaN;
  if (fn === "SUM" || fn === "SOMMA") return vals.reduce((a, b) => a + b, 0);
  if (fn === "AVG" || fn === "MEDIA") return vals.reduce((a, b) => a + b, 0) / vals.length;
  if (fn === "MIN") return Math.min(...vals);
  if (fn === "MAX") return Math.max(...vals);
  return NaN;
}
function evalFormula(expr, table, row, tables, seen) {
  let s = expr;
  s = s.replace(/T(\d+)\.([A-Za-z]+)/g, (m, n, L) => { const t = tables[parseInt(n, 10) - 1]; if (!t) return "NaN"; const cid = colIdByLetter(t, L); if (cid == null) return "NaN"; const v = columnAgg(t, "SUM", cid, tables); return isNaN(v) ? "NaN" : String(v); });
  s = s.replace(/(SOMMA|SUM|MEDIA|AVG|MIN|MAX|CONTA|COUNT)\(\s*([A-Za-z]+)\s*\)/gi, (m, fn, L) => { const cid = colIdByLetter(table, L); if (cid == null) return "NaN"; const v = columnAgg(table, fn.toUpperCase(), cid, tables); return isNaN(v) ? "NaN" : String(v); });
  s = s.replace(/[A-Za-z]+/g, (L) => { const cid = colIdByLetter(table, L); if (cid == null) return "NaN"; const v = cellNumber(table, row, cid, tables, seen); return isNaN(v) ? "0" : String(v); });
  if (/[A-Za-z]/.test(s)) return NaN;
  if (s.trim() === "" || !/^[0-9+\-*/().\s]*$/.test(s)) return NaN;
  try { const val = Function('"use strict";return (' + s + ")")(); return (typeof val === "number" && isFinite(val)) ? val : NaN; }
  catch { return NaN; }
}
const columnTotal = (table, colId, tables) => columnAgg(table, "SUM", colId, tables);
function displayValue(table, row, colId, tables) {
  const raw = (row.cells && row.cells[colId]) || "";
  if (isFormula(raw)) { const v = cellNumber(table, row, colId, tables, new Set()); return isNaN(v) ? "#ERR" : fmtEuro(v); }
  if (raw.trim() === "") return "";
  const n = toNumber(raw);
  return isNaN(n) ? raw : fmtEuro(n);
}
const DEFAULT_COLS = ["Banca", "Saldo", "Crediti", "Spesa", "Descrizione"];
const normalizeTable = (t) => ({
  id: t.id || uid(),
  title: t.title || "Tabella",
  columns: (t.columns || []).map((c) => ({ id: c.id || uid(), name: c.name || "" })),
  rows: (t.rows || []).map((r) => ({ id: r.id || uid(), cells: r.cells || {} })),
});
function parseCsv(text, title) {
  const clean = text.replace(/^\ufeff/, "");
  const firstLine = clean.split(/\r?\n/)[0] || "";
  const delim = (firstLine.split(";").length >= firstLine.split(",").length) ? ";" : ",";
  const lines = clean.split(/\r?\n/).filter((l) => l.length > 0);
  const parseLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
      else { if (ch === '"') q = true; else if (ch === delim) { out.push(cur); cur = ""; } else cur += ch; }
    }
    out.push(cur); return out;
  };
  const rowsArr = lines.map(parseLine);
  const header = rowsArr[0] || [];
  const columns = header.map((h) => ({ id: uid(), name: String(h || "").trim() || "Colonna" }));
  const rows = rowsArr.slice(1).map((arr) => { const cells = {}; columns.forEach((c, i) => { cells[c.id] = String(arr[i] == null ? "" : arr[i]); }); return { id: uid(), cells }; });
  return { id: uid(), title: (title || "Importato").replace(/\.(csv|xlsx|xls)$/i, ""), columns: columns.length ? columns : DEFAULT_COLS.map((n) => ({ id: uid(), name: n })), rows };
}
function aoaToTable(aoa, title) {
  if (!aoa || !aoa.length) return null;
  const header = aoa[0] || [];
  const columns = header.map((h, i) => ({ id: uid(), name: String(h == null ? "" : h).trim() || colLetter(i) }));
  const conv = (v) => (typeof v === "number" ? String(v).replace(".", ",") : String(v == null ? "" : v));
  const rows = aoa.slice(1).map((arr) => { const cells = {}; columns.forEach((c, i) => { cells[c.id] = conv(arr[i]); }); return { id: uid(), cells }; });
  return { id: uid(), title: title || "Foglio", columns, rows };
}

export default function Excel() {
  const { user } = useAuth();
  const cid = user?.company_id;
  const [tables, setTables] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | idle
  const [focused, setFocused] = useState(null); // {tid,rid,cid}
  const [applyDlg, setApplyDlg] = useState(null); // {tid,cid,formula,colName}
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/excel", { params: { company_id: cid } }); setTables((r.data.tables || []).map(normalizeTable)); }
      catch { /* noop */ }
      finally { setLoaded(true); }
    })();
  }, [cid]);

  const doSave = useCallback(async (next) => {
    try { await api.put("/excel", { company_id: cid, tables: next }); setSaveState("saved"); }
    catch (e) { setSaveState("idle"); toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  }, [cid]);

  const commit = useCallback((next) => {
    setTables(next);
    setSaveState("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSave(next), 900);
  }, [doSave]);

  const saveNow = () => { if (timerRef.current) clearTimeout(timerRef.current); setSaveState("saving"); doSave(tables); };

  // ---------- mutations ----------
  const addTable = () => {
    const base = tables.length ? tables[tables.length - 1].columns.map((c) => ({ id: uid(), name: c.name })) : DEFAULT_COLS.map((n) => ({ id: uid(), name: n }));
    const rows = Array.from({ length: 3 }, () => ({ id: uid(), cells: {} }));
    commit([...tables, { id: uid(), title: "Nuova tabella", columns: base, rows }]);
  };
  const delTable = (tid) => commit(tables.filter((t) => t.id !== tid));
  const setTitle = (tid, v) => commit(tables.map((t) => t.id === tid ? { ...t, title: v } : t));
  const setColName = (tid, colId, v) => commit(tables.map((t) => t.id === tid ? { ...t, columns: t.columns.map((c) => c.id === colId ? { ...c, name: v } : c) } : t));
  const addColumn = (tid) => commit(tables.map((t) => t.id === tid ? { ...t, columns: [...t.columns, { id: uid(), name: "Colonna " + colLetter(t.columns.length) }] } : t));
  const delColumn = (tid, colId) => commit(tables.map((t) => t.id === tid ? { ...t, columns: t.columns.filter((c) => c.id !== colId), rows: t.rows.map((r) => { const cells = { ...r.cells }; delete cells[colId]; return { ...r, cells }; }) } : t));
  const addRow = (tid) => commit(tables.map((t) => t.id === tid ? { ...t, rows: [...t.rows, { id: uid(), cells: {} }] } : t));
  const delRow = (tid, rid) => commit(tables.map((t) => t.id === tid ? { ...t, rows: t.rows.filter((r) => r.id !== rid) } : t));
  const setCell = (tid, rid, colId, v) => commit(tables.map((t) => t.id === tid ? { ...t, rows: t.rows.map((r) => r.id === rid ? { ...r, cells: { ...r.cells, [colId]: v } } : r) } : t));
  const applyFormulaToColumn = (tid, colId, formula) => commit(tables.map((t) => t.id === tid ? { ...t, rows: t.rows.map((r) => ({ ...r, cells: { ...r.cells, [colId]: formula } })) } : t));

  const onCellBlur = (t, rid, colId, colName) => {
    setFocused(null);
    const raw = ((t.rows.find((r) => r.id === rid)?.cells || {})[colId] || "");
    if (isFormula(raw) && t.rows.length > 1) setApplyDlg({ tid: t.id, cid: colId, formula: raw.trim(), colName });
  };

  // ---------- import / export ----------
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (fileRef.current) fileRef.current.value = ""; if (!file) return;
    const name = file.name.toLowerCase();
    try {
      let newTables = [];
      if (name.endsWith(".csv")) { const text = await file.text(); newTables = [parseCsv(text, file.name)]; }
      else { const buf = await file.arrayBuffer(); const wb = XLSX.read(buf, { type: "array" }); newTables = wb.SheetNames.map((sn) => aoaToTable(XLSX.utils.sheet_to_json(wb.Sheets[sn], { header: 1, raw: true, defval: "" }), sn)).filter(Boolean); }
      if (!newTables.length) { toast.error("Nessuna tabella trovata nel file"); return; }
      commit([...tables, ...newTables]);
      toast.success(`Importate ${newTables.length} tabella/e`);
    } catch (err) { toast.error("Errore durante l'import del file"); }
  };
  const tableToAoa = (t) => {
    const header = t.columns.map((c) => c.name);
    const rows = t.rows.map((r) => t.columns.map((c) => {
      const raw = (r.cells && r.cells[c.id]) || "";
      if (isFormula(raw)) { const v = cellNumber(t, r, c.id, tables, new Set()); return isNaN(v) ? "#ERR" : v; }
      if (raw.trim() === "") return "";
      const n = toNumber(raw); return isNaN(n) ? raw : n;
    }));
    const totals = t.columns.map((c) => { const v = columnTotal(t, c.id, tables); return isNaN(v) ? "" : v; });
    return [header, ...rows, totals];
  };
  const exportXlsx = (t) => { const ws = XLSX.utils.aoa_to_sheet(tableToAoa(t)); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, (t.title || "Tabella").slice(0, 31)); XLSX.writeFile(wb, (t.title || "tabella") + ".xlsx"); };
  const exportCsv = (t) => {
    const aoa = tableToAoa(t);
    const lines = aoa.map((row) => row.map((cell) => { let s = (typeof cell === "number") ? String(cell).replace(".", ",") : String(cell == null ? "" : cell); if (/[";\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"'; return s; }).join(";"));
    const csv = "\ufeff" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = (t.title || "tabella") + ".csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="excel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Table2 size={22} className="text-emerald-600" /> Excel</h1>
          <p className="text-sm text-muted-foreground">Fogli di calcolo liberi, con più tabelle, formule tra celle e tabelle, totali automatici e import/export XLSX/CSV.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold flex items-center gap-1.5 mr-1" data-testid="excel-save-state">
            {saveState === "saving" ? <span className="text-amber-600">Salvataggio…</span> : saveState === "saved" ? <span className="text-emerald-600 flex items-center gap-1"><Check size={14} /> Salvato</span> : <span className="text-muted-foreground">Non salvato</span>}
          </span>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onImport} data-testid="excel-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="excel-import-btn"><Upload size={15} className="mr-1" /> Importa file</Button>
          <Button variant="outline" onClick={saveNow} data-testid="excel-save-now"><Save size={15} className="mr-1" /> Salva ora</Button>
          <Button onClick={addTable} data-testid="excel-add-table" className="font-semibold"><Plus size={15} className="mr-1" /> Nuova tabella</Button>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground bg-secondary/40 border rounded-lg px-3 py-2" data-testid="excel-help">
        Formule: <b>=B-D</b> (lettere colonne). Funzioni: <b>=SOMMA(B)</b>, MEDIA, MIN, MAX, CONTA. Tra tabelle: <b>=T2.B</b>. Operatori + − * / ( ).
      </div>

      {loaded && tables.length === 0 && (
        <div className="gr-card p-10 text-center text-muted-foreground" data-testid="excel-empty">
          Nessuna tabella. Crea la prima con <b>Nuova tabella</b> o importa un file .xlsx/.csv.
        </div>
      )}

      {tables.map((t, ti) => (
        <div key={t.id} className="gr-card p-4 overflow-hidden" data-testid={`excel-table-${t.id}`}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="shrink-0 inline-flex items-center justify-center h-6 px-2 rounded-md bg-emerald-600 text-white text-xs font-bold" data-testid={`excel-code-${t.id}`}>T{ti + 1}</span>
            <input value={t.title} onChange={(e) => setTitle(t.id, e.target.value)} data-testid={`excel-title-${t.id}`} className="font-head font-bold text-lg bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none px-1 min-w-[140px] flex-1" />
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => exportXlsx(t)} data-testid={`excel-export-xlsx-${t.id}`}><FileSpreadsheet size={14} className="mr-1" /> XLSX</Button>
              <Button size="sm" variant="outline" onClick={() => exportCsv(t)} data-testid={`excel-export-csv-${t.id}`}><FileText size={14} className="mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={() => addColumn(t.id)} data-testid={`excel-add-col-${t.id}`}><Plus size={14} className="mr-0.5" /> Colonna</Button>
              <Button size="sm" variant="outline" onClick={() => addRow(t.id)} data-testid={`excel-add-row-${t.id}`}><Plus size={14} className="mr-0.5" /> Riga</Button>
              <Button size="sm" variant="outline" onClick={() => delTable(t.id)} data-testid={`excel-del-table-${t.id}`} className="text-destructive border-destructive/40"><Trash2 size={14} /></Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="border-collapse text-sm min-w-full">
              <thead>
                <tr>
                  <th className="w-10 bg-muted/60 border px-1 py-1 text-[11px] text-muted-foreground font-semibold sticky left-0">#</th>
                  {t.columns.map((c, ci) => (
                    <th key={c.id} className="bg-muted/60 border px-1 py-1 min-w-[130px]">
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] font-bold text-muted-foreground w-4 text-center">{colLetter(ci)}</span>
                        <input value={c.name} onChange={(e) => setColName(t.id, c.id, e.target.value)} data-testid={`excel-colname-${t.id}-${c.id}`} className="flex-1 min-w-0 bg-transparent font-semibold text-xs outline-none px-1 py-0.5 rounded focus:bg-white" />
                        <button onClick={() => delColumn(t.id, c.id)} data-testid={`excel-del-col-${t.id}-${c.id}`} className="text-muted-foreground/50 hover:text-destructive shrink-0" title="Elimina colonna"><Trash2 size={12} /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, ri) => (
                  <tr key={r.id} className={ri % 2 ? "bg-secondary/30" : "bg-white"}>
                    <td className="border px-1 py-0.5 text-center text-[11px] text-muted-foreground sticky left-0 bg-inherit">
                      <div className="flex items-center gap-0.5 justify-center">
                        <span>{ri + 1}</span>
                        <button onClick={() => delRow(t.id, r.id)} data-testid={`excel-del-row-${t.id}-${r.id}`} className="text-muted-foreground/40 hover:text-destructive" title="Elimina riga"><Trash2 size={11} /></button>
                      </div>
                    </td>
                    {t.columns.map((c) => {
                      const isFoc = focused && focused.tid === t.id && focused.rid === r.id && focused.cid === c.id;
                      const raw = (r.cells && r.cells[c.id]) || "";
                      const disp = displayValue(t, r, c.id, tables);
                      const err = disp === "#ERR";
                      const numeric = raw.trim() !== "" && (isFormula(raw) || !isNaN(toNumber(raw)));
                      return (
                        <td key={c.id} className="border p-0">
                          <input
                            value={isFoc ? raw : disp}
                            onChange={(e) => setCell(t.id, r.id, c.id, e.target.value)}
                            onFocus={() => setFocused({ tid: t.id, rid: r.id, cid: c.id })}
                            onBlur={() => onCellBlur(t, r.id, c.id, c.name)}
                            data-testid={`excel-cell-${t.id}-${r.id}-${c.id}`}
                            className={`w-full px-2 py-1 bg-transparent outline-none focus:bg-emerald-50 ${err ? "text-destructive font-semibold" : (isFormula(raw) ? "text-blue-700" : "")} ${(!isFoc && numeric) ? "text-right tabnum" : ""}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-emerald-50/70 font-semibold">
                  <td className="border px-1 py-1 text-center text-emerald-700 sticky left-0 bg-emerald-50/70">Σ</td>
                  {t.columns.map((c) => {
                    let cnt = 0; for (const rr of t.rows) { if (!isNaN(cellNumber(t, rr, c.id, tables, new Set()))) cnt++; }
                    const tot = columnTotal(t, c.id, tables);
                    return (<td key={c.id} className="border px-2 py-1 text-right tabnum text-emerald-800" data-testid={`excel-total-${t.id}-${c.id}`}>{cnt === 0 || isNaN(tot) ? "" : fmtEuro(tot)}</td>);
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={!!applyDlg} onOpenChange={(v) => !v && setApplyDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Applicare a tutta la colonna?</DialogTitle>
            <DialogDescription>Vuoi copiare la formula <b className="font-mono">{applyDlg?.formula}</b> in tutte le righe della colonna «{applyDlg?.colName}»?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setApplyDlg(null)} data-testid="excel-apply-col-no">No, solo qui</Button>
            <Button className="w-full sm:w-auto font-semibold" onClick={() => { applyFormulaToColumn(applyDlg.tid, applyDlg.cid, applyDlg.formula); setApplyDlg(null); toast.success("Formula applicata alla colonna"); }} data-testid="excel-apply-col-yes">Applica a tutta la colonna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
